import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, Trophy } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import AwardCard from '../components/AwardCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AwardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [awards, setAwards] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    tier: '',
    minStars: ''
  });

  useEffect(() => {
    fetchAwards();
  }, [filters]);

  // Fetch winners once on mount (tier=gold)
  useEffect(() => {
    axios.get(`${API_URL}/api/awards?tier=gold`)
      .then((res) => setWinners(res.data || []))
      .catch(() => setWinners([]));
  }, []);

  const fetchAwards = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.minStars) params.append('min_stars', filters.minStars);
      if (search) params.append('search', search);

      const response = await axios.get(`${API_URL}/api/awards?${params.toString()}`);
      setAwards(response.data);
    } catch (error) {
      console.error('Error fetching awards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAwards();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'category') {
      if (value) {
        setSearchParams({ category: value });
      } else {
        setSearchParams({});
      }
    }
  };

  const clearFilters = () => {
    setFilters({ category: '', tier: '', minStars: '' });
    setSearch('');
    setSearchParams({});
  };

  const hasActiveFilters = filters.category || filters.tier || filters.minStars || search;

  const categoryTabs = [
    { value: '', label: 'All' },
    { value: 'hotel', label: 'Hotels' },
    { value: 'resort', label: 'Resorts' },
    { value: 'destination', label: 'Destinations' }
  ];

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4">
            Award Winners
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-medium text-white mb-4">
            Travel Awards
          </h1>
          <p className="text-white/60 max-w-2xl">
            Explore our collection of award-winning hotels, resorts, and destinations from around the world.
          </p>
        </div>

        {/* Featured Award Winners */}
        {winners.length > 0 && (
          <section className="mb-14" data-testid="featured-winners">
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="w-5 h-5 text-[#D4AF37]" />
              <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">Featured Award Winners</p>
              <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {winners.map((w) => <AwardCard key={w.id} award={w} />)}
            </div>
          </section>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categoryTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleFilterChange('category', tab.value)}
              data-testid={`category-tab-${tab.value || 'all'}`}
              className={`category-pill ${
                filters.category === tab.value
                  ? 'category-pill-active'
                  : 'category-pill-inactive'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="Search by name, location, or country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 bg-[#121212] border-white/10 text-white placeholder:text-white/40 focus:border-[#D4AF37]"
                data-testid="search-input"
              />
            </div>
          </form>

          <div className="flex gap-3">
            <Select
              value={filters.tier}
              onValueChange={(value) => handleFilterChange('tier', value)}
            >
              <SelectTrigger 
                className="w-[140px] h-12 bg-[#121212] border-white/10 text-white"
                data-testid="tier-filter"
              >
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.minStars}
              onValueChange={(value) => handleFilterChange('minStars', value)}
            >
              <SelectTrigger 
                className="w-[140px] h-12 bg-[#121212] border-white/10 text-white"
                data-testid="stars-filter"
              >
                <SelectValue placeholder="Stars" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all">All Stars</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4+ Stars</SelectItem>
                <SelectItem value="3">3+ Stars</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="h-12 border-white/10 text-white/70 hover:text-white hover:bg-white/5"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-white/50 text-sm">
            {loading ? 'Loading...' : `${awards.length} awards found`}
          </p>
        </div>

        {/* Awards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
                <div className="h-56 skeleton" />
                <div className="p-5 space-y-3">
                  <div className="h-6 skeleton rounded w-3/4" />
                  <div className="h-4 skeleton rounded w-1/2" />
                  <div className="h-4 skeleton rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : awards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award) => (
              <AwardCard key={award.id} award={award} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#121212] flex items-center justify-center">
              <Filter className="w-8 h-8 text-white/30" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No awards found</h3>
            <p className="text-white/50 mb-6">
              Try adjusting your filters or search terms
            </p>
            <Button
              onClick={clearFilters}
              className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]"
              data-testid="no-results-clear"
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
